import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { shopService, ShopContext } from '@/services/shopService'
import { staffService } from '@/services/staffService'
import { subscribeShopContextRefresh } from '@/lib/shopContextRefresh'

interface ShopPreferences {
  date_format: string
  currency: string
  timezone: string
  service_billing_mode: 'prepaid' | 'hybrid' | 'postpaid'
  service_credit_grant_policy: 'on_order' | 'on_confirmed'
}

const DEFAULT_PREFERENCES: ShopPreferences = {
  date_format: 'YYYY-MM-DD',
  currency: '',
  timezone: 'UTC',
  service_billing_mode: 'prepaid',
  service_credit_grant_policy: 'on_order',
}

export const useShopPreferences = () => {
  const { user } = useAuthStore()
  const [shop, setShop] = useState<ShopContext | null>(null)
  const [userProfile, setUserProfile] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadPreferences = useCallback(async () => {
    if (!user?.shop_id) {
      setShop(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)

      const shopResponse = await shopService.getShopContext(user.shop_id)
      if (shopResponse.success && shopResponse.data) {
        setShop(shopResponse.data)
      }

      if (!user.is_web_user) {
        try {
          const profile = await staffService.getMyProfile()
          setUserProfile(profile)
        } catch (profileError) {
          console.warn('Error loading user profile:', profileError)
        }
      }
    } catch (error) {
      console.error('Error loading shop preferences:', error)
    } finally {
      setIsLoading(false)
    }
  }, [user?.shop_id, user?.is_web_user])

  useEffect(() => {
    void loadPreferences()
  }, [loadPreferences])

  useEffect(() => subscribeShopContextRefresh(() => void loadPreferences()), [loadPreferences])

  const preferences: ShopPreferences = shop
    ? {
        date_format: (userProfile as any)?.date_format || shop.date_format || DEFAULT_PREFERENCES.date_format,
        currency: shop.currency || '',
        timezone: (userProfile as any)?.timezone || shop.timezone || DEFAULT_PREFERENCES.timezone,
        service_billing_mode:
          (shop.service_billing_mode as ShopPreferences['service_billing_mode']) ||
          DEFAULT_PREFERENCES.service_billing_mode,
        service_credit_grant_policy:
          (shop.service_credit_grant_policy as ShopPreferences['service_credit_grant_policy']) ||
          DEFAULT_PREFERENCES.service_credit_grant_policy,
      }
    : DEFAULT_PREFERENCES

  return {
    shop,
    preferences,
    isLoading,
    refresh: loadPreferences,
  }
}
